// Vendor Libs
import {
    cloneDeep,
    find,
    get,
    intersection,
    isArray,
    isEmpty,
    keys,
    map,
    now,
    omit,
    pick,
    pickBy,
    reduce,
    replace,
    set,
    trim,
} from 'lodash';
import {push} from 'redux-first-history';

// Lib App
import actionsServiceCaller from '../lib/app/actions-service-caller';

// Core Libs
import {formatDuration, parseDurationAs} from '../lib/core/date';
import {parseJson} from '../lib/core/object';

// Configs
import servicesFormConfig from '../../config/services-form-config';
import formFieldConfig from '../../config/form-field-config';

const formMiddleware = (store) => (next) => (action) => {
    const {dispatch, getState} = store;
    next(action);
    const nextState = getState();
    const actions = {
        AUTHENTICATE_LOGOUT: () => resetFormData(dispatch),
        DELETE_OFFERING_FULFILLED: () => updateFormData(dispatch, action.formName, nextState),
        FORM_REGISTER: () => formRegistered(dispatch, action.formName, nextState),
        FORM_RESET: () => handleFormReset(dispatch, action, nextState),
        FORM_SET_FIELD_VALUE: () => handleFormFieldUpdate(dispatch, action),
        FORM_SUBMIT_ASYNC_LOCATION: () => fetchAsyncLocation(dispatch, action),
        FORM_SUBMIT_FETCH: () => submitFormData(dispatch, action, nextState.formData),
        FORM_SUBMIT_FULFILLED: () => updateFormData(dispatch, action.formName, nextState),
        MAPPING_POST_FULFILLED: () => updateFormData(dispatch, action.formName, nextState),
        REPUBLISH_MEDIAITEM_FULFILLED: () => updateFormData(dispatch, action.formName, nextState),
        ROUTER_UPDATE: () => handleRouterUpdate(dispatch, action),
        SEARCH_DATA_FETCH_FULFILLED: () => handleProviderServiceDataUpdate(dispatch, action, nextState),
        STANDARDIZE_GENRE_FETCH_FULFILLED: () => updateGenreData(dispatch, action, nextState),
    };

    if (actions[action.type]) {
        return actions[action.type]();
    }
};

function formRegistered(dispatch, formName, nextState) {
    const {providerServiceDataKey} = servicesFormConfig[formName] || {};

    if (providerServiceDataKey) {
        const lastUpdated = get(nextState, ['servicesSearchData', providerServiceDataKey, 'lastUpdated']);

        if (!lastUpdated) {
            dispatch({
                servicesDataKey: providerServiceDataKey,
                type: 'SEARCH_DATA_REGISTER_STORE',
            });
        } else if (lastUpdated < now() - 1000) {
            dispatch({
                servicesDataKey: providerServiceDataKey,
                type: 'SEARCH_DATA_REFRESH',
            });
        }
    }
}

function fetchAsyncLocation(dispatch, {formName, location}) {
    const dispatchFetched = (jsonResponse) => {
        if (find(get(jsonResponse, 'content', {}), ['status', 'RUNNING'])) {
            dispatch({
                formName,
                location,
                response: jsonResponse,
                type: 'FORM_SUBMIT_ASYNC_LOCATION',
            });
        } else {
            dispatch({
                async: true,
                formName,
                response: parseJson(get(jsonResponse, 'content.0.result')),
                type: 'FORM_SUBMIT_FULFILLED',
            });
        }
    };
    const dispatchError = (error) => {
        dispatch({
            async: true,
            formErrors: error,
            formName,
            type: 'FORM_SUBMIT_REJECTED',
        });
    };
    const actionParams = {
        servicesConfig: servicesFormConfig.async,
        dispatchFetched,
        dispatchError,
    };
    dispatch({
        formName,
        type: 'FORM_SUBMIT_ASYNC',
    });

    setTimeout(() => actionsServiceCaller(actionParams, {}, location), 2725);
}

function handleRouterUpdate(dispatch, {location: {pathname = ''} = {}, routerParams: {token} = {}}) {
    if (pathname.startsWith('/reset-password')) {
        dispatch({
            type: 'FORM_SET_FIELD_VALUE',
            fieldName: 'token',
            formName: 'resetPassword',
            value: token,
        });
    }
}

function getValuesFromProviderForm(servicesDataKey, formData) {
    const {providerFormName, providerFormFields} = get(servicesFormConfig, servicesDataKey, {});
    return reduce(
        providerFormFields,
        (obj, {fieldName, valueKey}) => {
            set(obj, valueKey, get(get(formData, [providerFormName, 'values'], {}), fieldName));
            return obj;
        },
        {}
    );
}

function submitFormData(dispatch, {async, formName, servicesDataKey, noAuthenticationRequired}, formData) {
    const values = formatFormDataForSubmit(formName, cloneDeep(get(formData, [formName, 'values'], {})));
    const dispatchFetched = (jsonResponse, response) => {
        const status = get(response, 'status');
        const location = get(response, 'headers.location');

        if (status === 202 && location) {
            dispatch({
                formName,
                location: location.replace(/^\/+/g, ''),
                response: jsonResponse,
                type: 'FORM_SUBMIT_ASYNC_LOCATION',
            });
        } else {
            dispatch({
                formName,
                response: jsonResponse,
                type: 'FORM_SUBMIT_FULFILLED',
            });
        }
    };
    const dispatchError = (error) => {
        dispatch({
            formErrors: error,
            formName,
            type: 'FORM_SUBMIT_REJECTED',
        });
    };
    const actionParams = {
        servicesConfig: {
            ...servicesFormConfig[servicesDataKey || formName],
        },
        dispatchFetched,
        dispatchError,
        noAuthenticationRequired,
    };
    dispatch({
        formName,
        type: 'FORM_SUBMIT',
    });

    if (async) {
        set(actionParams.servicesConfig, 'getParams.async', 'true');
    }

    return actionsServiceCaller(
        actionParams,
        {...values, ...getValuesFromProviderForm(servicesDataKey || formName, formData)},
        get(formData, [formName, 'customPath'])
    );
}

function formatFormDataForSubmit(formName, values) {
    // Fix runtime so the stored value is correct in the store.
    if (values.runtime) {
        values.runtime = parseDurationAs(values.runtime, 'seconds');
    }

    if (formName === 'editShell' || (formName === 'createShell' && values.echostarContentType !== 'SRE')) {
        values.seriesTitle = values.title;
    } else if (formName === 'flaggedMetadataStatus') {
        delete values.inProgress;
        delete values.fixed;
    } else if (formName === 'flaggedMetadataComments') {
        return values.amsIssue;
    } else if (formName === 'resetPassword') {
        delete values.passwordConfirm;
    } else if ((formName === 'editMediaItemOffering' || formName === 'createMediaItemOffering') && values.streetDate) {
        values.streetDate = `${values.streetDate}T12:00:00Z`;
    } else if ((formName === 'editNetwork' || formName === 'createNetwork') && values.tmsAutomappingEnabled) {
        values.tmsAutomappingEnabled = values.tmsAutomappingEnabled.toString() === 'true';
    }

    return values;
}

function updateFormData(dispatch, formName, nextState) {
    const {providerServiceDataKey, updateServiceDataKeys} = servicesFormConfig[formName] || {};

    map([providerServiceDataKey, ...(updateServiceDataKeys || [])], (servicesDataKey) => {
        if (servicesDataKey && nextState.servicesSearchData[servicesDataKey]) {
            dispatch({
                servicesDataKey,
                type: 'SEARCH_DATA_REFRESH',
            });
        }
    });
}

function resetFormData(dispatch) {
    dispatch({
        resetStore: true,
        type: 'FORM_RESET',
    });
}

function handleFormReset(dispatch, {formName, editing}, nextState) {
    if (editing) {
        const {[formName]: {providerServiceDataKey} = {}} = servicesFormConfig;
        const {servicesSearchData: {[providerServiceDataKey]: {searchData: {content: [searchData] = []} = {}}} = {}} =
            nextState;

        setFormDataFromProvider(dispatch, formName, searchData);
    }
}

function handleFormFieldUpdate(dispatch, {formName, value}) {
    if (formName === 'selectNetworkForDisplayPathModifiers' && !isEmpty(value)) {
        dispatch(push('/display-path-modifiers'));

        dispatch({
            formName: 'displayPathModifierDetail',
            resetValues: false,
            submitted: false,
            type: 'FORM_RESET',
        });

        dispatch({
            actionShouldFetch: true,
            selectRecord: true,
            servicesDataKey: 'displayPathModifiers',
            type: 'SEARCH_DATA_REFRESH',
        });
    } else if (formName === 'selectNetworkForDisplayPaths' && !isEmpty(value)) {
        dispatch(push('/display-paths'));

        dispatch({
            formName: 'displayPathDetail',
            resetValues: false,
            submitted: false,
            type: 'FORM_RESET',
        });

        dispatch({
            actionShouldFetch: true,
            selectRecord: true,
            servicesDataKey: 'displayPaths',
            type: 'SEARCH_DATA_REFRESH',
        });
    }
}

function handleProviderServiceDataUpdate(
    dispatch,
    {searchData: {content: [searchData] = []} = {}, servicesDataKey},
    nextState
) {
    const dependentFormNames = intersection(
        keys(pickBy(servicesFormConfig, (config) => config.providerServiceDataKey === servicesDataKey)),
        keys(nextState.formData),
        keys(formFieldConfig)
    );

    map(dependentFormNames, (formName) => {
        setFormDataFromProvider(dispatch, formName, searchData);
    });
}

function setFormDataFromProvider(dispatch, formName, searchData) {
    const {[formName]: {addFields = [], customPathFields, fields = [], fieldsPath} = {}} = formFieldConfig;

    if (!isEmpty(fields)) {
        let values = pick(fieldsPath ? get(searchData, fieldsPath) : searchData, fields);

        map(addFields, (fieldData) => {
            if (fieldData.overwrite || isEmpty(get(values, fieldData.name))) {
                set(values, fieldData.name, fieldData.value);
            }
        });

        // Custom fields
        if (formName === 'editShell') {
            values.rating = values.rating ? trim(values.rating.split('-').join('')) : undefined;
            values.genres = isArray(values.genres) ? values.genres : [];
            values.runtime = formatDuration(values.runtime);
            values.episodeNumber = values.echostarContentType === 'SRE' ? values.episodeNumber || 0 : undefined;
        }

        if (formName === 'editDisplayPath' && values.amsProgram !== undefined) {
            values.echostarId = values.amsProgram.echostarId;
            values = omit(values, ['amsProgram']);
        }

        dispatch({
            formName,
            resetValues: true,
            type: 'FORM_SET_FIELD_VALUES',
            values,
        });
    }

    setCustomPath(dispatch, formName, customPathFields, searchData);
}

function setCustomPath(dispatch, formName, customPathFields, searchData) {
    const {path} = servicesFormConfig[formName] || {};
    const newPath =
        path.indexOf('{') !== -1 ? `/${replace(path, /{(.*)}/g, (_match, field) => get(searchData, field))}` : '';
    const pathSuffix = customPathFields
        ? `${map(customPathFields, (customPathField) => get(searchData, customPathField)).join('/')}`
        : '';
    const customPath = newPath && pathSuffix ? `${newPath}/${pathSuffix}` : newPath || pathSuffix;

    if (customPath) {
        dispatch({
            formName,
            customPath,
            type: 'SET_FORM_CUSTOM_PATH',
        });
    }
}

const areArraysEqual = (a, b) =>
    a.length === b.length &&
    a.every((value, index) => value === b[index]);

function updateGenreData(dispatch, {formName, response}, nextState) {
    const existingGenres = get(nextState, ['formData', formName, 'values', 'genres']);
    const standardizedGenres = response?.standardizedGenres;
    const originalGenres = response?.originalGenres;
    const unmappedGenres = response?.unmappedGenres;
    let combinedGenres;

    if ((existingGenres?.length <= 0) || (originalGenres?.length <= 0)) {
        return;
    }

    if (!areArraysEqual(existingGenres, originalGenres)) {
        return;
    }

    if (unmappedGenres?.length > 0) {
        combinedGenres = [...standardizedGenres, ...unmappedGenres];
    } else {
        combinedGenres = standardizedGenres;
    }

    dispatch({
            fieldName: 'genres',
            formName: formName,
            type: 'FORM_SET_FIELD_VALUE',
            value: combinedGenres,
    });
}

export default formMiddleware;
